import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DeleteConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  dealerships = [], 
  isBulk = false 
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleConfirm = async () => {
    if (isBulk && confirmText !== 'LÖSCHEN') {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error deleting dealership(s):', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  if (!isOpen) return null;

  const dealershipCount = Array.isArray(dealerships) ? dealerships?.length : 1;
  const dealershipName = !Array.isArray(dealerships) ? dealerships?.name : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {isBulk ? 'Autohäuser löschen' : 'Autohaus löschen'}
              </h2>
              <p className="text-sm text-muted-foreground">
                Diese Aktion kann nicht rückgängig gemacht werden
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-error mb-2">Warnung</h3>
                <p className="text-sm text-foreground">
                  {isBulk ? (
                    <>
                      Sie sind dabei, <strong>{dealershipCount} Autohäuser</strong> zu löschen. 
                      Alle zugehörigen Daten, einschließlich Aufträge und Kontakte, werden ebenfalls gelöscht.
                    </>
                  ) : (
                    <>
                      Sie sind dabei, das Autohaus <strong>"{dealershipName}"</strong> zu löschen. 
                      Alle zugehörigen Daten, einschließlich Aufträge und Kontakte, werden ebenfalls gelöscht.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Dealership List (for bulk delete) */}
          {isBulk && dealerships?.length > 0 && (
            <div className="bg-muted rounded-lg p-4 max-h-32 overflow-y-auto">
              <h4 className="font-medium text-foreground mb-2">Zu löschende Autohäuser:</h4>
              <ul className="space-y-1">
                {dealerships?.slice(0, 5)?.map((dealership) => (
                  <li key={dealership?.id} className="text-sm text-muted-foreground flex items-center">
                    <Icon name="Building2" size={14} className="mr-2" />
                    {dealership?.name} ({dealership?.brand})
                  </li>
                ))}
                {dealerships?.length > 5 && (
                  <li className="text-sm text-muted-foreground">
                    ... und {dealerships?.length - 5} weitere
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Confirmation Input (for bulk delete) */}
          {isBulk && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Geben Sie "LÖSCHEN" ein, um zu bestätigen:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="LÖSCHEN"
              />
            </div>
          )}

          <div className="bg-muted rounded-lg p-3">
            <h4 className="font-medium text-foreground mb-1">Folgende Daten werden gelöscht:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center">
                <Icon name="Check" size={14} className="mr-2 text-error" />
                Autohaus-Informationen
              </li>
              <li className="flex items-center">
                <Icon name="Check" size={14} className="mr-2 text-error" />
                Kontaktdaten
              </li>
              <li className="flex items-center">
                <Icon name="Check" size={14} className="mr-2 text-error" />
                Zugehörige Aufträge
              </li>
              <li className="flex items-center">
                <Icon name="Check" size={14} className="mr-2 text-error" />
                Service-Historie
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isDeleting}
            disabled={isBulk && confirmText !== 'LÖSCHEN'}
          >
            <Icon name="Trash2" size={16} className="mr-2" />
            {isDeleting ? 'Lösche...' : (isBulk ? `${dealershipCount} löschen` : 'Löschen')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmModal;